import { queryOptions } from '@tanstack/react-query'
import { apiFetch } from '#/api/client'

export type UserStatus =
  | 'active'
  | 'provisional'
  | 'invited'
  | 'suspended'
  | 'deactivated'

// Live WorkOS invitation state. `state`/`expires_at` are null when the
// best-effort fetch failed; only present for still-invited users.
export type InvitationInfo = {
  id: string
  state: string | null
  expires_at: string | null
}

export type OrgUser = {
  uuid: string
  display_name: string | null
  email: string | null
  status: UserStatus
  // Role slug: the staged RBAC role while provisional/invited, and the user's
  // current WorkOS role once active. Null only when unknown.
  role: string | null
  // ISO timestamps. Optional: only present once the backend `GET /users`
  // surfaces them (see the created_at / last_signed_in_at follow-up).
  created_at?: string | null
  last_signed_in_at?: string | null
  invitation: InvitationInfo | null
}

// The RBAC roles an admin may stage on an invite. Mirrors the backend's
// INVITABLE_ROLES; internal_admin is Annwn-internal and never invitable. There
// is no roles endpoint, so this list is the frontend's source of truth.
export const INVITABLE_ROLES = ['contributor', 'reviewer', 'org_admin'] as const
export type InvitableRole = (typeof INVITABLE_ROLES)[number]

type UserListResponse = { data: Array<OrgUser> }

export const userKeys = {
  all: ['users'] as const,
}

export const usersQueryOptions = queryOptions({
  queryKey: userKeys.all,
  queryFn: async ({ signal }) => {
    const res = await apiFetch<UserListResponse>({ path: '/users', signal })
    return res.data
  },
})

export type CreateUserInput = {
  email: string
  display_name: string
  role: InvitableRole
}

export function createUser(input: CreateUserInput) {
  return apiFetch<OrgUser>({ method: 'POST', path: '/users', json: input })
}

export function inviteUser(userUuid: string) {
  return apiFetch<{ invitation: InvitationInfo }>({
    method: 'POST',
    path: `/users/${encodeURIComponent(userUuid)}/invite`,
  })
}

export function revokeInvitation(userUuid: string) {
  return apiFetch<{ invitation: InvitationInfo }>({
    method: 'DELETE',
    path: `/users/${encodeURIComponent(userUuid)}/invite`,
  })
}

// DELETE /users/{uuid} soft-suspends: the user is set to 'suspended' (kept for
// work attribution and still listed), their WorkOS membership is deactivated /
// pending invitation revoked. It never hard-deletes.
export function suspendUser(userUuid: string) {
  return apiFetch<unknown>({
    method: 'DELETE',
    path: `/users/${encodeURIComponent(userUuid)}`,
  })
}
